import { makeStyles } from '@map/theme';
import usePrefersReducedMotion from '@shared/hooks/usePrefersReducedMotion';
import usePrevious from '@shared/hooks/usePrevious';
import { Location } from 'history';
import { Children, cloneElement, ReactElement } from 'react';
import { RouteProps, Switch } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

// https://easings.net/#easeOutSine
const easing = 'cubic-bezier(0.61, 1, 0.88, 1)';
const duration = 350;

const useStyles = makeStyles(() => ({
  slider: {
    height: '100%',
    display: 'grid',
    '& > *': {
      gridArea: '1 / 1 / 2 / 2',
    },
  },
  fade: {
    '& *:not(:only-child)': {
      ['&.back-enter-active, &.back-exit-active, &.next-enter-active, &.next-exit-active']: {
        transition: `opacity ${duration}ms ${easing}`,
      },
    },
    overflow: 'hidden',
    // back
    '& .back-enter': {
      opacity: 0,
    },
    '& .back-enter-active': {
      opacity: 1,
    },
    '& .back-exit': {
      opacity: 1,
    },
    '& .back-exit-active': {
      opacity: 0,
    },

    // next
    '& .next-enter': {
      opacity: 0,
    },
    '& .next-enter-active': {
      opacity: 1,
    },
    '& .next-exit': {
      opacity: 1,
    },
    '& .next-exit-active': {
      opacity: 0,
    },
  },
  slide: {
    '& *:not(:only-child)': {
      ['&.back-enter-active, &.back-exit-active, &.next-enter-active, &.next-exit-active']: {
        transition: `transform ${duration}ms ${easing}`,
      },
    },
    overflow: 'hidden',
    // back
    '& .back-enter': {
      transform: 'translateX(-100%)',
    },
    '& .back-enter-active': {
      transform: 'translateX(0)',
    },
    '& .back-exit': {
      transform: 'translateX(0)',
    },
    '& .back-exit-active': {
      transform: 'translate(100%)',
    },

    // next
    '& .next-enter': {
      transform: 'translateX(100%)',
    },
    '& .next-enter-active': {
      transform: 'translateX(0)',
    },
    '& .next-exit': {
      transform: 'translateX(0)',
    },
    '& .next-exit-active': {
      transform: 'translateX(-100%)',
    },
  },
}));

type Props = {
  variant?: 'slide' | 'fade' | 'none';
  location: Location;
  children: Array<ReactElement<RouteProps>>;
};

const SlideRoutes = ({ variant: variantProp, location, children }: Props) => {
  const { classes, cx } = useStyles();
  const { pathname } = location;
  const prefersReducedMotion = usePrefersReducedMotion();

  const variant = variantProp ?? (prefersReducedMotion ? 'fade' : 'slide');

  const paths = Children.map<RegExp, Props['children'][number]>(
    children,
    ({ props }) => new RegExp(`^${props.path}${props.exact ? '$' : ''}`),
  );
  const key = paths.find((p) => pathname.match(p))?.toString() || pathname;

  const prevKey = usePrevious(key);

  if (variant === 'none') {
    return <Switch>{children}</Switch>;
  }

  let direction = 'next';
  if (prevKey !== key) {
    const currentIndex = paths.findIndex((p) => prevKey === p.toString());
    const nextIndex = paths.findIndex((p) => key === p.toString());
    if (currentIndex >= 0 && nextIndex >= 0 && nextIndex < currentIndex) {
      direction = 'back';
    }
  }

  return (
    <TransitionGroup
      className={cx(classes.slider, variant === 'fade' ? classes.fade : classes.slide)}
      childFactory={(child: ReactElement) => cloneElement(child, { classNames: direction })}
    >
      <CSSTransition key={key} timeout={duration}>
        <Switch location={location}>{children}</Switch>
      </CSSTransition>
    </TransitionGroup>
  );
};

export default SlideRoutes;
